import React from 'react';
import { motion } from 'framer-motion';
import SafeIcon from '../common/SafeIcon';
import SEOHead from '../components/SEOHead';
import * as FiIcons from 'react-icons/fi';

const { FiArrowLeft, FiZap, FiShoppingCart, FiCreditCard, FiTruck, FiUsers, FiBarChart, FiCalendar } = FiIcons;

const IntegrationsPage = () => {
  const integrationCategories = [
    { category: 'E-Commerce', icon: FiShoppingCart, color: 'red', integrations: ['Shopify', 'WooCommerce', 'Magento', 'PrestaShop', 'eBay', 'Amazon'] },
    { category: 'Payment', icon: FiCreditCard, color: 'red', integrations: ['PayPal', 'Stripe', 'Klarna', 'SEPA', 'Kreditkarten', 'Sofortüberweisung'] },
    { category: 'Zeiterfassung', icon: FiBarChart, color: 'red', integrations: ['Toggl', 'Harvest', 'TimeTracker', 'Clockodo', 'Timely', 'RescueTime'] },
    { category: 'CRM & Marketing', icon: FiUsers, color: 'red', integrations: ['HubSpot', 'Salesforce', 'Mailchimp', 'ActiveCampaign', 'Pipedrive', 'Zendesk'] },
    { category: 'Logistik', icon: FiTruck, color: 'red', integrations: ['DHL', 'UPS', 'FedEx', 'Hermes', 'GLS', 'DPD'] },
    { category: 'Automatisierung', icon: FiZap, color: 'red', integrations: ['Zapier', 'Microsoft Power Automate', 'IFTTT', 'Integromat', 'n8n', 'Automate.io'] }
  ];

  return (
    <>
      <SEOHead 
        title="140+ Integrationen für Lexware Office | Smarte Buchhaltung Online" 
        description="Lexware Office bietet über 140 sofort nutzbare Schnittstellen zu E-Commerce, CRM, Zeiterfassung und anderen Business-Tools. Alle Integrationen inklusive ohne Aufpreis."
        keywords="lexware office integrationen, buchhaltung schnittstellen, shopify integration, paypal buchhaltung, datev schnittstelle, crm integration, e-commerce buchhaltung"
        canonical="https://smarte-buchhaltung.online/#/integrationen"
      />
      <div className="min-h-screen bg-gray-50 pt-20 pb-16">
        <div className="container mx-auto px-6 lg:px-8">
          {/* Header */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6 }} 
            className="text-center mb-16"
          >
            <motion.a 
              href="#/" 
              whileHover={{ scale: 1.05 }}
              className="inline-flex items-center text-red-500 hover:text-red-600 mb-6"
            >
              <SafeIcon icon={FiArrowLeft} className="mr-2" />
              Zurück zur Startseite
            </motion.a>
            <h1 className="text-4xl lg:text-6xl font-bold text-gray-900 mb-6">
              Über 140 Integrationen – fast ohne zusätzlichen Aufwand
            </h1>
            <p className="text-xl text-gray-600 max-w-3xl mx-auto mb-8">
              Lexware Office lässt sich mit über 140 Systemen verbinden – von E-Commerce und Rechnungsstellung über CRM bis zur Zeiterfassung. Die Schnittstellen sind sofort nutzbar und werden kontinuierlich erweitert. So bleibt deine Buchhaltung nicht isoliert, sondern fügt sich nahtlos in deine bestehenden Arbeitsabläufe ein. Keine zusätzliche IT, kein Umweg, kein Mehraufwand.
            </p>
          </motion.div>

          {/* Integrations Grid */}
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mb-16">
            {integrationCategories.map((category, index) => (
              <motion.div 
                key={index}
                initial={{ opacity: 0, y: 30 }} 
                animate={{ opacity: 1, y: 0 }} 
                transition={{ duration: 0.6, delay: index * 0.1 }}
                className="bg-[#F3F1EE] p-6 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300"
              >
                <div className="flex items-center mb-6">
                  <div className="mr-4 text-[#E30613]">
                    <SafeIcon icon={category.icon} className="text-3xl" />
                  </div>
                  <h3 className="text-lg font-bold text-gray-900">{category.category}</h3>
                </div>
                <div className="space-y-2">
                  {category.integrations.map((integration, intIndex) => (
                    <div key={intIndex} className="flex items-center text-sm text-gray-600">
                      <div className="w-2 h-2 bg-[#E30613] rounded-full mr-2"></div>
                      {integration}
                    </div>
                  ))}
                </div>
                <div className="mt-4 pt-4 border-t border-gray-100">
                  <p className="text-xs text-gray-500">+ viele weitere Integrationen verfügbar</p>
                </div>
              </motion.div>
            ))}
          </div>

          {/* CTA Section */}
          <motion.div 
            initial={{ opacity: 0, y: 30 }} 
            animate={{ opacity: 1, y: 0 }} 
            transition={{ duration: 0.6, delay: 0.4 }}
            className="text-center"
          >
            <div className="bg-[#F3F1EE] p-8 rounded-xl shadow-lg max-w-3xl mx-auto">
              <h3 className="text-2xl font-bold mb-4 text-[#1A1A1A]">
                Alle Integrationen bereits enthalten
              </h3>
              <p className="text-[#1A1A1A] mb-6">
                Die über 140 Integrationen in Lexware Office sind direkt nutzbar – ohne zusätzliche Aktivierung oder Erweiterungen. Du verbindest deine Buchhaltung einfach mit den Systemen, die du bereits nutzt. Für einzelne Partner-Tools können eigene Kosten entstehen, die Integration selbst ist jedoch bereits in Lexware Office enthalten.
              </p>
              <div className="flex justify-center">
                <motion.a 
                  href="https://nextcloud.managershop.com/index.php/apps/appointments/pub/Gb05r41McyYVyGQE/form" 
                  target="_blank" 
                  rel="noopener noreferrer"
                  whileHover={{ scale: 1.05 }} 
                  whileTap={{ scale: 0.95 }}
                  className="bg-[#E30613] hover:bg-[#C10511] text-white px-6 py-3 rounded-lg font-semibold transition-all duration-300 flex items-center space-x-2"
                >
                  <SafeIcon icon={FiCalendar} />
                  <span>Integrationen besprechen</span>
                </motion.a>
              </div>
            </div>
          </motion.div>
        </div>
      </div>
    </>
  );
};

export default IntegrationsPage;